// Fork https://github.com/meritt/rubles
// rubles.ts

const wordZero = 'ноль';

// Define the words used for number conversion
const words: string[][] = [
  [
    '',
    'один',
    'два',
    'три',
    'четыре',
    'пять',
    'шесть',
    'семь',
    'восемь',
    'девять',
    'десять',
    'одиннадцать',
    'двенадцать',
    'тринадцать',
    'четырнадцать',
    'пятнадцать',
    'шестнадцать',
    'семнадцать',
    'восемнадцать',
    'девятнадцать',
  ],
  [
    '',
    '',
    'двадцать',
    'тридцать',
    'сорок',
    'пятьдесят',
    'шестьдесят',
    'семьдесят',
    'восемьдесят',
    'девяносто',
  ],
  [
    '',
    'сто',
    'двести',
    'триста',
    'четыреста',
    'пятьсот',
    'шестьсот',
    'семьсот',
    'восемьсот',
    'девятьсот',
  ],
];

// Define the plural forms for rubles
const rusRubles: string[] = ['рубль', 'рубля', 'рублей'];
const belRubles: string[] = ['белорусский рубль', 'белорусских рубля', 'белорусских рублей'];

// Utility function to convert input to float
const toFloat = (input: string | number): number => {
  return parseFloat(input.toString());
};

// Function to determine the correct plural form based on the count
const plural = (count: number, options: string[]): string => {
  if (options.length !== 3) {
    throw new Error('Plural options must have exactly 3 forms.');
  }

  count = Math.abs(count) % 100;
  const rest = count % 10;

  if (count > 10 && count < 20) {
    return options[2];
  }

  if (rest > 1 && rest < 5) {
    return options[1];
  }

  if (rest === 1) {
    return options[0];
  }

  return options[2];
};

// Function to parse each segment of the number
const parseNumber = (number: string, count: number, currCode: string): string => {
  let numeral = '';

  if (number.length === 3) {
    const first = number.charAt(0);
    number = number.substring(1);
    numeral += `${words[2][toFloat(first)]} `;
  }

  if (toFloat(number) < 20) {
    numeral += `${words[0][toFloat(number)]} `;
  } else {
    const first = number.charAt(0);
    const second = number.substring(1);
    numeral += `${words[1][toFloat(first)]} ${words[0][toFloat(second)]} `;
  }

  if (count === 0) {
    switch (currCode) {
      case 'BYN':
        numeral += plural(toFloat(number), belRubles);
        break;
      case 'RU':
      default:
        numeral += plural(toFloat(number), rusRubles);
    }
  } else if (count === 1) {
    if (numeral.trim() !== '') {
      numeral += plural(toFloat(number), ['тысяча ', 'тысячи ', 'тысяч ']);
      numeral = numeral.replace('один ', 'одна ').replace('два ', 'две ');
    }
  } else if (count === 2) {
    if (numeral.trim() !== '') {
      numeral += plural(toFloat(number), ['миллион ', 'миллиона ', 'миллионов ']);
    }
  } else if (count === 3) {
    numeral += plural(toFloat(number), ['миллиард ', 'миллиарда ', 'миллиардов ']);
  } else if (count === 4) {
    // Для поддержки триллионов
    numeral += plural(toFloat(number), ['триллион ', 'триллиона ', 'триллионов ']);
  }

  return numeral;
};

// Function to parse the decimal part (kopeks)
const parseDecimals = (number: number): string => {
  const text = plural(number, ['копейка', 'копейки', 'копеек']);
  let numStr: string;

  if (number === 0) {
    numStr = '00';
  } else if (number < 10) {
    numStr = `0${number}`;
  } else {
    numStr = number.toString();
  }

  return ` ${numStr} ${text}`;
};

// Main function to convert number to words with currency
export const rubles = (input: string | number, currCode: 'RU' | 'BYN' = 'RU'): string | false => {
  if (input === null || input === undefined) {
    return false;
  }

  let num: number;

  if (typeof input === 'number') {
    num = input;
  } else if (typeof input === 'string') {
    num = toFloat(input.replace(',', '.'));
    if (isNaN(num)) {
      return false;
    }
  } else {
    return false;
  }

  if (num <= 0) {
    return false;
  }

  const fixedNum = num.toFixed(2);
  let integerPart: string;
  let decimals: string | undefined;

  if (fixedNum.includes('.')) {
    const [intPart, decPart] = fixedNum.split('.');
    integerPart = intPart;
    decimals = decPart;
  } else {
    integerPart = fixedNum;
    decimals = undefined;
  }

  let numeral = '';
  const numberStr = integerPart;
  let length = numberStr.length - 1;
  let parts = '';
  let count = 0;

  while (length >= 0) {
    const digit = numberStr.charAt(length);
    parts = digit + parts;

    if ((parts.length === 3 || length === 0) && !isNaN(toFloat(parts))) {
      numeral = parseNumber(parts, count, currCode) + numeral;
      parts = '';
      count++;
    }

    length--;
  }

  numeral = numeral.replace(/\s+/g, ' ').trim();

  // Если нет никакого числа то будет только транслитерация
  // Добавляем нуль в начало строки
  if (numeral === 'рублей') {
    numeral = `${wordZero} ${numeral}`;
  }

  if (decimals !== undefined) {
    numeral += parseDecimals(toFloat(decimals));
  }

  return numeral;
};
